import { useAppSelector } from '@app/redux/hooks';
import { getInvoices } from '@app/services/plans';
import { apiInstance } from '@app/services/api';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';

export const useYourCurrentPlan = () => {
  const auth = useAppSelector((state) => state.auth);
  const business = useAppSelector((state) => state.business);
  const businessSubscription = auth?.user?.subscriptions?.find(
    (sub) => sub.businessId === business?.selected?.id
  );

  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [orderBy, setOrderBy] = useState<
    'amount' | 'status' | 'description' | 'payment_method' | 'date'
  >('date');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
  const [startingAfter, setStartingAfter] = useState<string | undefined>(
    undefined
  );

  const { data: invoicesResponse, isLoading } = useQuery({
    queryKey: [
      'get-invoices',
      businessSubscription?.stripeSubscriptionId,
      currentPage,
      limit,
      orderBy,
      orderDirection,
      startingAfter,
    ],
    queryFn: () =>
      getInvoices({
        subId: businessSubscription?.stripeSubscriptionId as string,
        orderBy,
        orderDirection,
        limit,
        startingAfter,
      }),
    enabled: !!businessSubscription,
  });

  const handlePageChange = (page?: number) => {
    if (page !== undefined) {
      setCurrentPage(page);
      // Reset startingAfter when changing pages
      setStartingAfter(undefined);
    }
  };

  const handleLimitChange = (newLimit?: number) => {
    if (newLimit !== undefined) {
      setLimit(newLimit);
      setCurrentPage(1);
      setStartingAfter(undefined);
    }
  };

  const handleSort = (field: string) => {
    const validFields = [
      'amount',
      'status',
      'description',
      'payment_method',
      'date',
    ];
    if (!validFields.includes(field)) return;

    const typedField = field as
      | 'amount'
      | 'status'
      | 'description'
      | 'payment_method'
      | 'date';

    if (orderBy === typedField) {
      // Toggle direction if same field
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction
      setOrderBy(typedField);
      setOrderDirection('desc');
    }

    setCurrentPage(1);
    setStartingAfter(undefined);
  };

  const cancelSubscription = async () => {
    if (!businessSubscription?.stripeSubscriptionId) {
      throw new Error('No subscription ID available');
    }

    return apiInstance
      .post('/subscription/cancel', {
        subId: businessSubscription.stripeSubscriptionId,
      })
      .then(() => toast.success('Subscription cancelled successfully'))
      .catch(() => toast.error('Error cancelling subscription'));
  };

  return {
    businessSubscription,
    invoicesResponse,
    isLoading,
    currentPage,
    limit,
    orderBy,
    orderDirection,
    handlePageChange,
    handleLimitChange,
    handleSort,
    cancelSubscription,
  };
};
